import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Typography,
  Tag,
  Popconfirm
} from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined, UserDeleteOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import { TournamentService } from '@/services/tournament.service';
import { logger } from '@/utils/logger';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

interface ClubRegistration {
  id: number;
  clubId: number;
  tournamentId: number;
  club: {
    id: number;
    name: string;
    city?: string;
    region?: string;
  };
  status: 'pending' | 'confirmed' | 'rejected';
  confirmed: boolean;
  registeredAt: string;
  confirmedAt?: string;
  participantCount?: number;
  confirmer?: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

interface RegistrationConfirmationProps {
  tournamentId: number;
}

const RegistrationConfirmation: React.FC<RegistrationConfirmationProps> = ({ tournamentId }) => {
  const [clubs, setClubs] = useState<ClubRegistration[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClubs();
  }, [tournamentId]);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const response: any = await apiService.get(`/tournaments/${tournamentId}/clubs`);
            
      // Обрабатываем разные форматы ответа
      let clubsData: ClubRegistration[] = [];
      
      if (response.data?.data) {
        clubsData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response.data)) {
        clubsData = response.data;
      } else if (response.data) {
        clubsData = [response.data];
      }
      
      // Преобразуем данные в нужный формат
      const formattedClubs = clubsData.map((club: any) => ({
        id: club.id || club.clubId || (club.club && club.club.id),
        clubId: club.clubId || club.club_id || (club.club && club.club.id),
        tournamentId: club.tournamentId || club.tournament_id || tournamentId,
        club: club.club || {
          id: club.clubId || club.club_id,
          name: club.name || club.clubName || 'Неизвестный клуб',
          city: club.city,
          region: club.region
        },
        status: club.status || (club.confirmed ? 'confirmed' : 'pending'),
        confirmed: club.confirmed || false,
        registeredAt: club.registeredAt || club.registered_at || club.createdAt,
        confirmedAt: club.confirmedAt || club.confirmed_at,
        participantCount: club.participantCount || 0,
        confirmer: club.confirmer || (club.confirmedBy ? {
          id: club.confirmedBy,
          firstName: club.confirmerFirstName || '',
          lastName: club.confirmerLastName || ''
        } : undefined)
      }));
      
      setClubs(formattedClubs);
    } catch (error: any) {
      logger.error('Error fetching clubs:', error);
      message.error('Не удалось загрузить клубы');
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (clubId: number) => {
    try {
      await apiService.put(`/tournaments/${tournamentId}/clubs/${clubId}/confirm`);
      message.success('Регистрация клуба подтверждена');
      fetchClubs();
    } catch (error: any) {
      logger.error('Error confirming club registration:', error);
      message.error(error?.response?.data?.message || 'Не удалось подтвердить регистрацию');
    }
  };

  const handleReject = async (clubId: number) => {
    try {
      await apiService.put(`/tournaments/${tournamentId}/clubs/${clubId}/reject`);
      message.success('Регистрация клуба отклонена');
      fetchClubs();
    } catch (error: any) {
      logger.error('Error rejecting club registration:', error);
      message.error(error?.response?.data?.message || 'Не удалось отклонить регистрацию');
    }
  };

  const handleUnregister = async (clubId: number) => {
    try {
      const result = await TournamentService.unregisterClub(tournamentId, clubId);
      if (result.bracketsReformed > 0) {
        message.success(
          `Клуб снят с турнира. Удалено участников: ${result.removedParticipants}. Пересобраны сетки в ${result.bracketsReformed} категории(ях).`
        );
      } else if (result.categoriesNeedingReform > 0) {
        message.success(
          `Клуб снят с турнира. Удалено участников: ${result.removedParticipants}. В ${result.categoriesNeedingReform} категории(ях) требуется переформировать сетки на странице жеребьёвки.`
        );
      } else {
        message.success(
          `Клуб снят с турнира. Удалено участников: ${result.removedParticipants}.`
        );
      }
      fetchClubs();
    } catch (error: any) {
      logger.error('Error unregistering club:', error);
      message.error(error?.response?.data?.message || 'Не удалось снять клуб с турнира');
    }
  };

  const getStatusTag = (status: string, confirmed: boolean) => {
    if (confirmed || status === 'confirmed') {
      return <Tag color="green">Подтверждена</Tag>;
    }
    if (status === 'rejected') {
      return <Tag color="red">Отклонена</Tag>;
    }
    return <Tag color="orange">Ожидает подтверждения</Tag>;
  };

  const columns: ColumnsType<ClubRegistration> = [
    {
      title: 'Название клуба',
      key: 'name',
      render: (_, record) => record.club?.name || '-',
      width: 200
    },
    {
      title: 'Город',
      key: 'city',
      render: (_, record) => record.club?.city || '-',
      width: 150
    },
    {
      title: 'Регион',
      key: 'region',
      render: (_, record) => record.club?.region || '-',
      width: 150
    },
    {
      title: 'Участников',
      key: 'participantCount',
      render: (_, record) => (record as any).participantCount || 0,
      width: 100
    },
    {
      title: 'Дата регистрации',
      key: 'registeredAt',
      render: (_, record) => record.registeredAt ? new Date(record.registeredAt).toLocaleDateString('ru-RU') : '-',
      width: 150
    },
    {
      title: 'Статус',
      key: 'status',
      render: (_, record) => getStatusTag(record.status, record.confirmed),
      width: 150
    },
    {
      title: 'Подтверждено',
      key: 'confirmedBy',
      render: (_, record) => {
        if (record.confirmed && record.confirmer) {
          return `${record.confirmer.firstName} ${record.confirmer.lastName}`;
        }
        if (record.confirmedAt) {
          return new Date(record.confirmedAt).toLocaleDateString('ru-RU');
        }
        return '-';
      },
      width: 150
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => {
        const isConfirmed = record.confirmed || record.status === 'confirmed';
        const isRejected = record.status === 'rejected';

        return (
          <Space>
            {!isConfirmed && !isRejected && (
              <>
                <Popconfirm
                  title="Подтвердить регистрацию клуба?"
                  onConfirm={() => handleConfirm(record.clubId)}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    Подтвердить
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="Отклонить регистрацию клуба?"
                  onConfirm={() => handleReject(record.clubId)}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    size="small"
                  >
                    Отклонить
                  </Button>
                </Popconfirm>
              </>
            )}
            {isConfirmed && (
              <>
                <Tag color="green">Подтверждено</Tag>
                <Popconfirm
                  title="Снять клуб с турнира? Все участники клуба будут удалены из турнира."
                  onConfirm={() => handleUnregister(record.clubId)}
                  okText="Да"
                  cancelText="Нет"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="default"
                    danger
                    size="small"
                    icon={<UserDeleteOutlined />}
                  >
                    Снять
                  </Button>
                </Popconfirm>
              </>
            )}
            {isRejected && <Tag color="red">Отклонено</Tag>}
          </Space>
        );
      },
      width: 200
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>Подтверждение регистраций клубов</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchClubs}
          loading={loading}
        >
          Обновить
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={clubs}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Всего клубов: ${total}`
          }}
        />
      </Card>
    </div>
  );
};

export default RegistrationConfirmation;

